<template>
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="160" height="160">
        <defs>
            <!-- Atmospheric halo: warm amber glow radiating outward from the rim -->
            <radialGradient id="tg-halo" cx="50%" cy="50%" r="50%">
                <stop offset="48%" stop-color="#c8903c" stop-opacity="0" />
                <stop offset="100%" stop-color="#c8903c" stop-opacity="0.14" />
            </radialGradient>
            <!-- Dome lighting: bright at center, dark vignette at rim -->
            <radialGradient id="tg-dome" cx="50%" cy="50%" r="50%">
                <stop offset="0%"   stop-color="white" stop-opacity="0.10" />
                <stop offset="55%"  stop-color="white" stop-opacity="0" />
                <stop offset="100%" stop-color="black" stop-opacity="0.22" />
            </radialGradient>
            <!-- Drop shadow -->
            <filter id="tg-shadow" x="-25%" y="-25%" width="150%" height="150%">
                <feDropShadow dx="0" dy="1.2" stdDeviation="2.5" flood-color="#000000" flood-opacity="0.38" />
            </filter>
        </defs>

        <!-- Atmospheric halo behind everything -->
        <circle cx="50" cy="50" r="50" fill="url(#tg-halo)" />

        <g :transform="`rotate(${noonRotation} 50 50)`" filter="url(#tg-shadow)">
            <!-- Pie slices -->
            <path v-for="(slice, i) in slices" :key="i" :d="slice.d" :fill="slice.fill" />

            <!-- Dome lighting overlay on segments -->
            <circle cx="50" cy="50" r="45" fill="url(#tg-dome)" />

            <!-- Double-edge rim: primary border + outer halo ring + inner highlight -->
            <circle cx="50" cy="50" r="46.6" fill="none" stroke="#9090a0" stroke-width="0.35" stroke-opacity="0.45" />
            <circle cx="50" cy="50" r="45"   fill="none" stroke="#9090a0" stroke-width="0.7" />
            <circle cx="50" cy="50" r="43.6" fill="none" stroke="white"   stroke-width="0.5" stroke-opacity="0.18" />

            <!-- Now line -->
            <line v-if="nowLine" :x1="50" :y1="50" :x2="nowLine.x2" :y2="nowLine.y2"
                  stroke="#ffc107" stroke-width="1.2" stroke-linecap="round" />

            <!-- Donut hole -->
            <circle cx="50" cy="50" r="20" fill="white" stroke="#9090a0" stroke-width="0.7" />

            <!-- Cardinal cross -->
            <line x1="50" y1="44.5" x2="50" y2="55.5" stroke="#9090a0" stroke-width="0.7" />
            <line x1="44.5" y1="50" x2="55.5" y2="50" stroke="#9090a0" stroke-width="0.7" />
            <text x="50"   y="41.2" text-anchor="middle" dominant-baseline="central" font-size="4" font-weight="bold" fill="#9090a0" :transform="`rotate(${-noonRotation} 50 41.2)`">N</text>
            <text x="50"   y="58.8" text-anchor="middle" dominant-baseline="central" font-size="4" font-weight="bold" fill="#9090a0" :transform="`rotate(${-noonRotation} 50 58.8)`">S</text>
            <text x="58.8" y="50"   text-anchor="middle" dominant-baseline="central" font-size="4" font-weight="bold" fill="#9090a0" :transform="`rotate(${-noonRotation} 58.8 50)`">E</text>
            <text x="41.2" y="50"   text-anchor="middle" dominant-baseline="central" font-size="4" font-weight="bold" fill="#9090a0" :transform="`rotate(${-noonRotation} 41.2 50)`">W</text>
        </g>
    </svg>
</template>

<script>
export default {
    name: 'TwilightGraph',
    props: {
        datalist: {
            type: Array,
            default: () => [],
        },
        types: {
            type: Array,
            default: () => [],
        },
        nowAngleDeg: {
            type: Number,
            default: null,
        },
    },
    computed: {
        slices() {
            const total = this.datalist.reduce((s, v) => s + v, 0)
            if (!total) return []
            const TYPE_COLORS = { 0: '#000000', 1: '#1a1a1a', 2: '#666666', 3: '#b3b3b3', 4: '#ffffff' }
            const cx = 50, cy = 50, r = 45
            let angle = -Math.PI / 2
            return this.datalist.map((value, i) => {
                if (!value) return null
                const arc = 2 * Math.PI * value / total
                const x1 = cx + r * Math.cos(angle)
                const y1 = cy + r * Math.sin(angle)
                angle += arc
                const x2 = cx + r * Math.cos(angle)
                const y2 = cy + r * Math.sin(angle)
                const d = `M ${cx} ${cy} L ${x1.toFixed(4)} ${y1.toFixed(4)} A ${r} ${r} 0 ${arc > Math.PI ? 1 : 0} 1 ${x2.toFixed(4)} ${y2.toFixed(4)} Z`
                const fill = TYPE_COLORS[this.types[i]] ?? '#888888'
                return { d, fill }
            }).filter(Boolean)
        },
        nowLine() {
            if (this.nowAngleDeg === null) return null
            const rad = this.nowAngleDeg * Math.PI / 180
            return {
                x2: (50 + 45 * Math.cos(rad)).toFixed(4),
                y2: (50 + 45 * Math.sin(rad)).toFixed(4),
            }
        },
        noonRotation() {
            const total = this.datalist.reduce((s, v) => s + v, 0)
            if (!total) return 0
            const dayIdx = this.types.indexOf(4)
            if (dayIdx < 0 || !this.datalist[dayIdx]) return 0
            const sumBefore = this.datalist.slice(0, dayIdx).reduce((s, v) => s + v, 0)
            const dayFrac = (sumBefore + this.datalist[dayIdx] / 2) / total
            return (Math.PI - 2 * Math.PI * dayFrac) * 180 / Math.PI
        },
    },
}
</script>
